import React from 'react';
import { Type, Calendar, ToggleLeft, List, Hash, FolderPlus, MessageSquare, Link } from 'lucide-react';
import { useEditor } from '../store/EditorContext';
import { FieldType, InputControl, Group } from '../types';
import { useDraggable } from '@dnd-kit/core';

interface DraggablePaletteItemProps {
  type: FieldType;
  label: string;
  icon: React.ReactNode;
  defaultControl: InputControl;
}

const PaletteItem: React.FC<DraggablePaletteItemProps> = ({ type, label, icon, defaultControl }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `palette-${type}`,
        data: {
            type: 'sidebar-item',
            fieldType: type,
            defaultControl,
            label
        }
    });

    return (
        <button
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className={`flex items-center justify-center p-2 rounded-lg transition-all cursor-grab active:cursor-grabbing hover:bg-slate-100 ${isDragging ? 'opacity-50 ring-2 ring-teal-500 bg-teal-50' : 'bg-transparent'}`}
            title={label}
        >
            <div className={`p-1.5 rounded-md shadow-sm border border-slate-200 ${isDragging ? 'bg-teal-100 text-teal-600 border-teal-200' : 'bg-white text-slate-600'}`}>
                {icon}
            </div>
        </button>
    );
};

export const BottomPalette: React.FC = () => {
  const { dispatch, generateId } = useEditor();

  const handleAddGroup = () => {
      const id = generateId('group');
      const newGroup: Group = {
          id,
          label: 'New Group',
          collapsible: false
      };
      dispatch({ type: 'ADD_GROUP', payload: newGroup });
  };

  const fieldTypes: DraggablePaletteItemProps[] = [
    { type: FieldType.STRING, label: 'Text', icon: <Type size={14} />, defaultControl: InputControl.TEXT },
    { type: FieldType.NUMBER, label: 'Number', icon: <Hash size={14} />, defaultControl: InputControl.NUMBER },
    { type: FieldType.DATE, label: 'Date', icon: <Calendar size={14} />, defaultControl: InputControl.DATEPICKER },
    { type: FieldType.BOOLEAN, label: 'Bool', icon: <ToggleLeft size={14} />, defaultControl: InputControl.CHECKBOX },
    { type: FieldType.ENUM, label: 'Select', icon: <List size={14} />, defaultControl: InputControl.SELECT },
    { type: FieldType.DIALOG, label: 'Dialog', icon: <MessageSquare size={14} />, defaultControl: InputControl.BUTTON },
    { type: FieldType.PARAMETER, label: 'Param', icon: <Link size={14} />, defaultControl: InputControl.READONLY },
  ];

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40">
        <div className="flex items-center gap-1 bg-white/90 backdrop-blur-md shadow-2xl border border-slate-200/60 p-1.5 rounded-full ring-1 ring-black/5">
             <button
                onClick={handleAddGroup}
                className="flex items-center justify-center p-2 rounded-lg hover:bg-teal-50 transition-all group"
                title="Add Group"
            >
                <div className="p-1.5 rounded-md bg-teal-50 text-teal-600 shadow-sm border border-teal-100 group-hover:bg-teal-100 group-hover:border-teal-200">
                    <FolderPlus size={14} />
                </div>
            </button>
            
            <div className="w-px h-6 bg-slate-200 mx-1" />

            {fieldTypes.map((item) => (
                <PaletteItem key={item.type} {...item} />
            ))}
        </div>
    </div>
  );
};